import React, { useState } from "react";

interface CallEntry {
  id: string;
  at: string;
  by: string;
  caller: string;
  callerType: string;
  phone: string;
  resident: string;
  priority: string;
  message: string;
  action: string;
  followUp: string;
  followUpBy: string;
  done: boolean;
}

const STORE_KEY = "call_log_records";

function loadCalls(): CallEntry[] {
  try {
    return JSON.parse(localStorage.getItem(STORE_KEY) || "[]");
  } catch (error) {
    return [];
  }
}

export default function CallLog({ user }: { user?: any }) {
  const [calls, setCalls] = useState<CallEntry[]>(loadCalls);
  const [caller, setCaller] = useState("");
  const [callerType, setCallerType] = useState("Family");
  const [phone, setPhone] = useState("");
  const [resident, setResident] = useState("");
  const [priority, setPriority] = useState("Routine");
  const [message, setMessage] = useState("");
  const [action, setAction] = useState("");
  const [followUp, setFollowUp] = useState("No");
  const [followUpBy, setFollowUpBy] = useState("");
  const [filter, setFilter] = useState<"all" | "open" | "done">("all");
  const [notice, setNotice] = useState("");

  const saveCall = () => {
    if (!caller || !message) {
      setNotice("Caller and message are required.");
      return;
    }

    const entry: CallEntry = {
      id: `call-${Date.now()}`,
      at: new Date().toISOString(),
      by: user && user.name ? user.name : "Unknown",
      caller,
      callerType,
      phone,
      resident,
      priority,
      message,
      action,
      followUp,
      followUpBy,
      done: false,
    };

    const next = [entry, ...calls];

    setCalls(next);
    localStorage.setItem(STORE_KEY, JSON.stringify(next));
    setNotice(`Call logged at ${new Date(entry.at).toLocaleString()}.`);
    setCaller("");
    setPhone("");
    setResident("");
    setMessage("");
    setAction("");
    setFollowUpBy("");
  };

  const toggleDone = (id: string) => {
    const next = calls.map(entry => (entry.id === id ? { ...entry, done: !entry.done } : entry));

    setCalls(next);
    localStorage.setItem(STORE_KEY, JSON.stringify(next));
  };

  const shown = calls.filter(entry => (filter === "all" ? true : filter === "open" ? !entry.done : entry.done));

  return (
    <div className="space-y-6 p-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold text-slate-900">Call Log</h2>
          <p className="text-sm text-slate-600">Every call logged with an automatic date and time stamp, action taken and follow-up tracking.</p>
        </div>
        <div className="flex gap-2">
          {(["all", "open", "done"] as const).map(value => (
            <button key={value} onClick={() => setFilter(value)} className={filter === value ? "rounded-lg bg-blue-900 px-4 py-2 text-sm font-medium text-white" : "rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700"}>
              {value === "all" ? "All" : value === "open" ? "Open follow-ups" : "Completed"}
            </button>
          ))}
        </div>
      </div>

      {notice ? <div className="rounded-lg bg-blue-50 p-3 text-sm text-blue-900">{notice}</div> : null}

      <div className="max-w-2xl space-y-3 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="grid gap-3 md:grid-cols-2">
          <input value={caller} onChange={event => setCaller(event.target.value)} placeholder="Caller name" className="rounded-lg border border-slate-300 px-3 py-2 text-sm" />
          <select value={callerType} onChange={event => setCallerType(event.target.value)} className="rounded-lg border border-slate-300 px-3 py-2 text-sm">
            <option>Family</option>
            <option>GP</option>
            <option>Pharmacy</option>
            <option>Local authority</option>
            <option>Hospital</option>
            <option>Other</option>
          </select>
          <input value={phone} onChange={event => setPhone(event.target.value)} placeholder="Phone number" className="rounded-lg border border-slate-300 px-3 py-2 text-sm" />
          <input value={resident} onChange={event => setResident(event.target.value)} placeholder="Resident concerned" className="rounded-lg border border-slate-300 px-3 py-2 text-sm" />
          <select value={priority} onChange={event => setPriority(event.target.value)} className="rounded-lg border border-slate-300 px-3 py-2 text-sm">
            <option>Routine</option>
            <option>Urgent</option>
          </select>
          <select value={followUp} onChange={event => setFollowUp(event.target.value)} className="rounded-lg border border-slate-300 px-3 py-2 text-sm">
            <option>No</option>
            <option>Yes</option>
          </select>
        </div>
        <textarea value={message} onChange={event => setMessage(event.target.value)} rows={3} placeholder="Message" className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" />
        <textarea value={action} onChange={event => setAction(event.target.value)} rows={2} placeholder="Action taken" className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" />
        <input value={followUpBy} onChange={event => setFollowUpBy(event.target.value)} placeholder="Follow up by (name and date)" className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" />
        <button onClick={saveCall} className="rounded-lg bg-blue-900 px-5 py-2 text-sm font-medium text-white">Log call</button>
      </div>

      <div className="space-y-3">
        {shown.map(entry => (
          <div key={entry.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="font-medium text-slate-900">{entry.caller} ({entry.callerType})</div>
              <div className="flex items-center gap-2">
                {entry.priority === "Urgent" ? <span className="rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700">URGENT</span> : null}
                {entry.followUp === "Yes" && !entry.done ? <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700">FOLLOW UP</span> : null}
              </div>
            </div>
            <div className="mt-1 text-xs text-slate-500">{new Date(entry.at).toLocaleString()} | Logged by {entry.by} | Phone {entry.phone || "-"} | Resident {entry.resident || "-"}</div>
            <div className="mt-2 text-sm text-slate-700">{entry.message}</div>
            {entry.action ? <div className="mt-1 text-sm text-slate-600">Action: {entry.action}</div> : null}
            {entry.followUp === "Yes" ? <div className="mt-1 text-sm text-slate-600">Follow up: {entry.followUpBy || "-"} {entry.done ? "(completed)" : ""}</div> : null}
            {entry.followUp === "Yes" ? (
              <button onClick={() => toggleDone(entry.id)} className="mt-2 rounded-lg border border-slate-300 px-3 py-1 text-sm">{entry.done ? "Reopen" : "Mark follow-up complete"}</button>
            ) : null}
          </div>
        ))}
        {shown.length === 0 ? <div className="text-sm text-slate-500">No calls logged yet.</div> : null}
      </div>
    </div>
  );
}